import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ERROR_COPY, EXPORT_COPY, SUPPORT_LINE, UNEXPECTED_COPY, scrubMessage } from "../../config/errorMessages";
import "./notify.css";

/**
 * The single place every user-facing message is rendered: colour, duration and wording policy live
 * here rather than in the hundreds of components that raise alerts.
 */

/* Errors last longer so they can be read, but both close on their own: making the user dismiss
   them is friction, and persistent toasts stack up and queue later messages behind the container
   limit. Hovering pauses the timer for anyone who needs longer. */
const ERROR_AUTO_CLOSE_MS = 10000;
const SUCCESS_AUTO_CLOSE_MS = 4000;
const LONG_SUCCESS_AUTO_CLOSE_MS = 6000;

/* Matched by identity, not keyword: otherwise rewording a message could flip it to a green
   success toast, since our copy need not contain any of the words below. */
const OWN_ERROR_COPY = new Set([...Object.values(ERROR_COPY), ...Object.values(EXPORT_COPY)].filter(Boolean));

/* Only reached for arbitrary backend text. */
const looksLikeError = (message = "") =>
    /error|fail|invalid|denied|wrong|unable|cannot|can't|not found|not allowed|oops|required|must be|mandatory|already exist|no data/i.test(
        message
    );

export const notifyError = (message, toastOptions = {}) => {
    const clean = scrubMessage(message);

    /* Tell the user what to do next, but only when the fault is ours and they cannot act on it —
       appending it to "Name is required" would be nonsense. No reference: it is in the logs, and a
       code on a toast that closes itself is noise the user cannot use. */
    const body = UNEXPECTED_COPY.has(clean) ? `${clean} ${SUPPORT_LINE}.` : clean;

    return toast.error(body, {
        /* Collapses identical concurrent failures into one toast. */
        toastId: `fca-error:${clean}`,
        autoClose: ERROR_AUTO_CLOSE_MS,
        pauseOnHover: true,
        ...toastOptions
    });
};

export const notifySuccess = (message, options = {}) => toast.success(message, { autoClose: SUCCESS_AUTO_CLOSE_MS, ...options });

/**
 * Preferred way to surface a failed request: uses the message the interceptor already resolved,
 * so the call site does not have to know which field its backend used.
 */
export const notifyApiError = error => notifyError(error?.friendlyMessage || error?.response?.data?.message);

/**
 * Back-compatible entry point for existing `showAlert(message, isLong)` callers. Severity is
 * inferred; migrated call sites should pass an explicit type or use the helpers above.
 */
export const notify = (message, isLong = "", type) => {
    const text = typeof message === "string" ? message.trim() : "";

    /* Nothing to show is a call-site bug, not something to alarm the user about. The old alert bar
       rendered these as an invisible empty strip, which is why they went unnoticed for years. */
    if (!text && !type) {
        if ((window.__ENV && window.__ENV.ENVIRONMENT) !== "production") {
            console.warn("[notify] called with no message; the call site has nothing to show.", new Error("blank alert").stack);
        }
        return undefined;
    }

    const kind = type || (OWN_ERROR_COPY.has(text) || looksLikeError(text) ? "error" : "success");
    if (kind === "error") return notifyError(text);
    return notifySuccess(text, isLong ? { autoClose: LONG_SUCCESS_AUTO_CLOSE_MS } : {});
};

/* Manual QA helper, excluded from production builds. From the browser console:
     __fcaNotify.success("Saved")
     __fcaNotify.error("Name is required")
     __fcaNotify.error(ERROR_COPY.SERVER)                                            */
if (process.env.NODE_ENV !== "production") {
    window.__fcaNotify = {
        error: notifyError,
        success: notifySuccess,
        api: notifyApiError,
        legacy: notify,
        ERROR_COPY
    };
}
